import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer, normalizeSectionId } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /** DCD 2026-11-04: time of the last keystroke in the search box (the cabinet-view fetch waits for a pause). */
  #dcdLastInput = 0;

  /**
   * DCD 2026-11-04: the pending or in-flight cabinet-view fetch, cancelled when a newer query starts.
   * @type {AbortController | null}
   */
  #dcdCabinetFetch = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    // DCD 2026-11-04: a new keystroke cancels the pending or in-flight cabinet-view fetch for the older query.
    this.refs.searchInput.addEventListener('input', this.#dcdOnInput, { signal });

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#dcdCabinetFetch?.abort();
  }

  #dcdOnInput = () => {
    this.#dcdLastInput = performance.now();
    this.#dcdCabinetFetch?.abort();
    this.#dcdCabinetFetch = null;
  };

  /**
   * DCD 2026-11-04: fetch this section from the cabinet view of /search once typing pauses (DCD_CABINET_PAUSE_MS after the
   * last keystroke). Resolves to the markup, null on failure, or DCD_STALE when a newer query started first.
   * @param {any} dcdSearch - window.DCDSearch
   * @param {string} searchTerm
   * @returns {Promise<string | null | typeof DCD_STALE>}
   */
  #dcdFetchCabinets(dcdSearch, searchTerm) {
    this.#dcdCabinetFetch?.abort();
    const controller = new AbortController();
    this.#dcdCabinetFetch = controller;
    const { signal } = controller;

    const url = new URL(dcdSearch.cabUrl(searchTerm), location.origin);
    url.searchParams.set('section_id', normalizeSectionId(this.dataset.sectionId || ''));
    url.searchParams.sort();
    const key = url.toString();
    const cached = dcdCabinetCache.get(key);
    if (cached) return Promise.resolve(cached);

    const wait = Math.max(0, DCD_CABINET_PAUSE_MS - (performance.now() - this.#dcdLastInput));
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        fetch(key, { signal })
          .then((response) => (response.ok ? response.text() : null))
          .then((html) => {
            if (html) dcdCabinetCache.set(key, html);
            resolve(signal.aborted ? DCD_STALE : html);
          })
          .catch(() => resolve(signal.aborted ? DCD_STALE : null));
      }, wait);
      signal.addEventListener(
        'abort',
        () => {
          clearTimeout(timer);
          resolve(DCD_STALE);
        },
        { once: true }
      );
    });
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          // DCD CX-08: a query without a part word opens the cabinet view (window.DCDSearch, snippets/dcd-search.liquid)
          const dcdSearch = /** @type {any} */ (window).DCDSearch;
          if (dcdSearch && dcdSearch.wants(this.refs.searchInput.value)) {
            event.preventDefault();
            window.location.href = dcdSearch.cabUrl(this.refs.searchInput.value);
            return;
          }
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    // DCD CX-08: for a query without a part word, also fetch this section from the cabinet view of /search
    // (product types Base, Wall, Tall, Vanity), and list those cabinets ahead of parts.
    // 2026-11-04: that fetch waits until typing pauses and is cancelled by the next keystroke (one request per pause,
    // not one per key); the dropdown is drawn once both responses are in, as before.
    const dcdSearch = /** @type {any} */ (window).DCDSearch;
    if (!(dcdSearch && searchTerm.length >= 2 && dcdSearch.wants(searchTerm))) {
      this.#dcdCabinetFetch?.abort();
      this.#dcdCabinetFetch = null;
    }
    const cabinetsPromise =
      dcdSearch && searchTerm.length >= 2 && dcdSearch.wants(searchTerm)
        ? this.#dcdFetchCabinets(dcdSearch, searchTerm)
        : Promise.resolve(null);

    Promise.all([sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url), cabinetsPromise])
      .then(([resultsMarkup, cabinetsMarkup]) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        if (cabinetsMarkup === DCD_STALE) return;

        if (cabinetsMarkup) resultsMarkup = dcdMergeCabinets(resultsMarkup, cabinetsMarkup);

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();
    this.#dcdCabinetFetch?.abort();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

/** DCD 2026-11-04: pause after the last keystroke before the cabinet-view fetch is sent. */
const DCD_CABINET_PAUSE_MS = 400;
/** DCD 2026-11-04: marks a cabinet-view fetch that a newer query replaced. */
const DCD_STALE = Symbol('dcd-stale');
/** @type {Map<string, string>} DCD 2026-11-04: cabinet-view markup by section URL, for this page view. */
const dcdCabinetCache = new Map();

/**
 * DCD CX-08: put cabinets ahead of parts in the dropdown's product list.
 * Order: cabinets from the suggest response, then cabinets from the cabinet view of /search, then the other suggest items.
 * @param {string} resultsMarkup - predictive search section HTML (suggest)
 * @param {string} cabinetsMarkup - the same section rendered on the cabinet view of /search
 * @returns {string}
 */
function dcdMergeCabinets(resultsMarkup, cabinetsMarkup) {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(resultsMarkup, 'text/html');
    const cab = parser.parseFromString(cabinetsMarkup, 'text/html');
    const cabinetItems = [...cab.querySelectorAll('#predictive-search-products li[data-dcd-kind="cab"]')];
    if (!cabinetItems.length) return resultsMarkup;

    const inner = doc.querySelector('[data-search-results]');
    if (!inner) return resultsMarkup;
    let list = doc.querySelector('#predictive-search-products ul');
    if (!list) {
      const block = cab.getElementById('predictive-search-products');
      if (!block) return resultsMarkup;
      const imported = /** @type {HTMLElement} */ (doc.importNode(block, true));
      const articles = doc.getElementById('predictive-search-article');
      const anchor = articles ? articles.closest('.predictive-search-results__inner > *') : null;
      if (anchor && anchor.parentElement === inner) inner.insertBefore(imported, anchor);
      else inner.appendChild(imported);
      doc.querySelector('.predictive-search-results__no-results')?.remove();
      list = imported.querySelector('ul');
      if (!list) return resultsMarkup;
    }

    const current = /** @type {HTMLElement[]} */ ([...list.querySelectorAll(':scope > li')]);
    const seen = new Set();
    /** @type {Node[]} */
    const out = [];
    /** @param {Element} li */
    const add = (li) => {
      const id = /** @type {HTMLElement} */ (li).dataset.dcdPid;
      if (id && seen.has(id)) return;
      if (id) seen.add(id);
      out.push(li.ownerDocument === doc ? li : doc.importNode(li, true));
    };
    current.filter((li) => li.dataset.dcdKind === 'cab').forEach(add);
    cabinetItems.forEach(add);
    current.filter((li) => li.dataset.dcdKind !== 'cab').forEach(add);
    list.replaceChildren(...out.slice(0, 8));
    if (out.length > 1) doc.querySelector('[data-single-result-url]')?.remove();
    return doc.body.innerHTML;
  } catch (e) {
    return resultsMarkup;
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
